/**
 * 题库数据管理模块
 * 从 JSON 文件加载题目数据，提供类型安全的访问接口
 */

import problemsData from './problems.json';

// ========================================
// 类型定义
// ========================================

/** 难度级别 */
export type Difficulty = 'easy' | 'medium' | 'hard';

/** 题目分类 */
export type Category = 'array' | 'string' | 'linked-list' | 'tree' | 'dp';

/** 编程语言 */
export type Language = 'javascript' | 'python' | 'java' | 'cpp';

/** 测试用例 */
export interface TestCase {
  expectedOutputs: string[];
  description: string;
}

/** 题目解答（多语言） */
export interface Solutions {
  javascript: string;
  python: string;
  java: string;
  cpp: string;
  [key: string]: string;  // 索引签名，支持动态访问
}

/** 题目完整信息 */
export interface Problem {
  id: string;
  name: string;
  difficulty: Difficulty;
  category: Category;
  description: string;
  tags: string[];           // 算法标签
  acceptanceRate: string;   // 通过率
  shortDescription: string; // 简短描述（卡片展示用）
  testCase: TestCase;
  solutions: Solutions;
}

/** 题目基本信息（用于列表展示） */
export interface ProblemInfo {
  name: string;
  difficulty: Difficulty;
  category: string;
  description?: string;
  tags?: string[];           // 算法标签
  acceptanceRate?: string;   // 通过率
  shortDescription?: string; // 简短描述
}

// ========================================
// 数据处理
// ========================================

// 从 JSON 加载题目列表
const problemsList: Problem[] = problemsData.problems as Problem[];

// 构建题目信息映射表 (兼容原有 PROBLEM_INFO 格式)
export const PROBLEM_INFO: Record<string, ProblemInfo> = {};
problemsList.forEach(p => {
  PROBLEM_INFO[p.id] = {
    name: p.name,
    difficulty: p.difficulty,
    category: p.category,
    description: p.description,
    tags: p.tags,
    acceptanceRate: p.acceptanceRate,
    shortDescription: p.shortDescription
  };
});

// 构建题目答案映射表 (兼容原有 PROBLEM_ANSWERS 格式)
export const PROBLEM_ANSWERS: Record<string, Record<string, string>> = {};
problemsList.forEach(p => {
  PROBLEM_ANSWERS[p.id] = p.solutions;
});

// 构建测试用例映射表 (兼容原有 PROBLEM_TEST_CASES 格式)
export const PROBLEM_TEST_CASES: Record<string, TestCase> = {};
problemsList.forEach(p => {
  PROBLEM_TEST_CASES[p.id] = p.testCase;
});

// 合并额外的测试用例（如 fibonacci, bubble-sort 等）
if (problemsData.testCasesExtra) {
  Object.entries(problemsData.testCasesExtra).forEach(([id, testCase]) => {
    PROBLEM_TEST_CASES[id] = testCase as TestCase;
  });
}

// ========================================
// 辅助函数
// ========================================

/**
 * 获取所有题目列表
 */
export function getAllProblems(): Problem[] {
  return problemsList;
}

/**
 * 根据ID获取题目
 */
export function getProblemById(id: string): Problem | undefined {
  return problemsList.find(p => p.id === id);
}

/**
 * 获取所有题目ID
 */
export function getAllProblemIds(): string[] {
  return problemsList.map(p => p.id);
}

/**
 * 按难度筛选题目
 */
export function getProblemsByDifficulty(difficulty: Difficulty): Problem[] {
  return problemsList.filter(p => p.difficulty === difficulty);
}

/**
 * 按分类筛选题目
 */
export function getProblemsByCategory(category: Category): Problem[] {
  return problemsList.filter(p => p.category === category);
}

/**
 * 获取题目数量统计
 */
export function getProblemStats(): {
  total: number;
  byDifficulty: Record<Difficulty, number>;
  byCategory: Record<string, number>;
} {
  const byDifficulty: Record<Difficulty, number> = { easy: 0, medium: 0, hard: 0 };
  const byCategory: Record<string, number> = {};

  problemsList.forEach(p => {
    byDifficulty[p.difficulty]++;
    byCategory[p.category] = (byCategory[p.category] || 0) + 1;
  });

  return {
    total: problemsList.length,
    byDifficulty,
    byCategory
  };
}

/**
 * 获取题目的参考答案
 */
export function getSolution(problemId: string, language: Language): string | undefined {
  const problem = getProblemById(problemId);
  return problem?.solutions[language];
}

/**
 * 获取题目的测试用例
 */
export function getTestCase(problemId: string): TestCase | undefined {
  return PROBLEM_TEST_CASES[problemId];
}

// 导出原始数据（供调试使用）
export { problemsList as problems };
